/**
 * Rating a Thunderstore package against the game version a Valheim server runs (TR-5406).
 *
 * The backend rates the customer's *selection* on every config read, including dependency capping,
 * and the panel uses that. This module exists for the other half of the page: the **browse results**,
 * which are catalogue rows the backend has never resolved and which therefore carry no rating. Once a
 * customer is searching 3,300 packages, "which of these are even built for my game version" is the
 * first question, and answering it only after they install something is answering it too late.
 *
 * 🚨 This is a mirror of `decline/game_profiles/valheim_compat.py::classify`, and the two must agree.
 * What keeps them honest is that the *era* is not duplicated: every date, tag and threshold comes
 * from `ValheimModsInfo.era`, which the backend builds from its own table. Only the comparison lives
 * here, and `frontend/tests/units/util/valheimModCompat.spec.ts` asserts the same cases the Python
 * tests do. Project Zomboid mirrors its `loadable()` check the same way, for the same reason.
 *
 * Dependency capping is deliberately **not** mirrored. A browse result is not installed, so nothing
 * it requires is installed either, and a rating that pretended otherwise would be about a selection
 * the customer has not made.
 */
import type { SteamWorkshopItem, ValheimGameEra } from "@/graphql";

export const COMPAT = {
  /** Carries the era's own update category. The author says it works. */
  ready: "ready",
  /** Released after the era's game version, but untagged. Nothing is claimed. */
  likelyOk: "likely_ok",
  /** Last released before the era's game version. */
  probablyOutdated: "probably_outdated",
  /** Missed two or more content patches, or requires something that did. */
  likelyBroken: "likely_broken",
  /** A library, an unmapped branch, or a row with no release date. */
  unknown: "unknown",
} as const;

export type Compatibility = (typeof COMPAT)[keyof typeof COMPAT];

/** The Thunderstore category for packages whose release date says nothing about compatibility. */
const LIBRARY_CATEGORY = "Libraries";

/**
 * Categories that describe game content. Mirrors `valheim_compat.CONTENT_CATEGORIES`.
 *
 * 🚨 The `Libraries` category alone is not usable: of the 38 packages carrying it, several are whole
 * modpacks and several are gameplay mods whose authors tick every box.
 * `Smoothbrain-CreatureLevelAndLootControl` (2025-05-25) declares `Tweaks`, `Enemies` and `Libraries`
 * together, and was silently exempted from every warning until this veto existed.
 */
const CONTENT_CATEGORIES = new Set([
  "Modpacks",
  "Tweaks",
  "Building",
  "Crafting",
  "Gear",
  "Enemies",
  "NPCs",
  "World Generation",
  "Transportation",
  "Vehicles",
  "Audio",
  "PvP",
  "Skins",
  "Language",
]);

/**
 * Infrastructure that declares no categories at all, so `LIBRARY_CATEGORY` cannot find it.
 *
 * Mirrors `valheim_compat.LIBRARY_FULL_NAMES`. `HookGenPatcher` is the reason both lists exist: last
 * released 2023-03-28, running fine on 13 services, and without this it lands in the red tier.
 */
const LIBRARY_FULL_NAMES = new Set([
  "ValheimModding-HookGenPatcher",
  "denikson-BepInExPack_Valheim",
  "BepInEx-BepInExPack_Valheim",
]);

/** Whether a catalogue row is exempt from the date heuristic. */
export function isLibrary(mod: Pick<SteamWorkshopItem, "valheim">): boolean {
  const meta = mod.valheim;
  if (!meta) {
    return false;
  }
  if (LIBRARY_FULL_NAMES.has(meta.fullName)) {
    return true;
  }
  const categories = (meta.categories ?? []).filter((name): name is string => !!name);
  return categories.includes(LIBRARY_CATEGORY) && !categories.some((name) => CONTENT_CATEGORIES.has(name));
}

/**
 * The release day of a catalogue row, as a `YYYY-MM-DD` string.
 *
 * ⚠️ Compared as strings, never as `Date` objects. `dateModified` arrives as an ISO timestamp and the
 * era dates as plain days; parsing both into `Date` drags the browser's timezone into a comparison
 * that is about calendar days, which flips the verdict for anything published within a day of a
 * patch. Lexicographic comparison of `YYYY-MM-DD` is exactly date order.
 */
export function releaseDay(mod: Pick<SteamWorkshopItem, "dateModified">): string | null {
  const raw = mod.dateModified;
  if (!raw) {
    return null;
  }
  const day = String(raw).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
}

/**
 * Rate one catalogue row against one game version.
 *
 * Ordered rather than scored, so each outcome is one sentence a tooltip can state.
 */
export function classify(
  mod: Pick<SteamWorkshopItem, "valheim" | "dateModified">,
  era: ValheimGameEra | null | undefined,
): Compatibility {
  if (!era || isLibrary(mod)) {
    return COMPAT.unknown;
  }
  const released = releaseDay(mod);
  if (!released) {
    return COMPAT.unknown;
  }
  // The author's own promise, checked before the date so a tagged package can never be demoted.
  if (era.tag && (mod.valheim?.categories ?? []).includes(era.tag)) {
    return COMPAT.ready;
  }
  const eraDay = String(era.released).slice(0, 10);
  if (released >= eraDay) {
    return COMPAT.likelyOk;
  }
  const previousDay = era.previousReleased ? String(era.previousReleased).slice(0, 10) : null;
  if (previousDay && released < previousDay) {
    return COMPAT.likelyBroken;
  }
  return COMPAT.probablyOutdated;
}

/**
 * A release day as the customer's locale writes it.
 *
 * The date is the whole substance of the tooltip ("last updated on 12 March 2026"), so it has to
 * read as a date and not as a timestamp.
 */
export function formatDay(day: string | null | undefined, locale: string): string {
  if (!day) {
    return "";
  }
  const parsed = new Date(`${String(day).slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(parsed.valueOf())
    ? ""
    : parsed.toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
}
